import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import i18n from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { createProject, deleteProject, updateProject } from "../api";
import {
  PERSONAL_PROJECT_SCOPE,
  type CreateProjectInput,
  type DeleteProjectInput,
  type ProjectListScope,
  type UpdateProjectInput,
} from "../types";
import { invalidateProjectsQueryScope } from "./useProjectsQuery";

const useProjectMutationScope = (): ProjectListScope => {
  const { currentUser } = useAuth();
  const { currentOrganization } = useOrganization();

  return {
    principalId: currentUser?.id ?? "",
    organizationId: currentOrganization?.id ?? PERSONAL_PROJECT_SCOPE,
  };
};

const useInvalidateMutationScope = () => {
  const queryClient = useQueryClient();

  return (scope: ProjectListScope) =>
    invalidateProjectsQueryScope(queryClient, scope);
};

export const useCreateProjectMutation = () => {
  const scope = useProjectMutationScope();
  const invalidateScope = useInvalidateMutationScope();

  return useMutation({
    mutationFn: (project: CreateProjectInput) =>
      createProject({ organizationId: scope.organizationId, project }),
    onSuccess: async () => {
      await invalidateScope(scope);
      toast.success(i18n.t("projects.projectCreatedSuccess"));
    },
    onError: () => {
      toast.error(i18n.t("projects.failedToCreateProject"));
    },
  });
};

export const useUpdateProjectMutation = () => {
  const scope = useProjectMutationScope();
  const invalidateScope = useInvalidateMutationScope();

  return useMutation({
    mutationFn: (project: UpdateProjectInput) =>
      updateProject({ organizationId: scope.organizationId, project }),
    onSuccess: async () => {
      await invalidateScope(scope);
      toast.success(i18n.t("projects.projectUpdatedSuccess"));
    },
    onError: () => {
      toast.error(i18n.t("projects.failedToUpdateProject"));
    },
  });
};

export const useDeleteProjectMutation = () => {
  const scope = useProjectMutationScope();
  const invalidateScope = useInvalidateMutationScope();

  return useMutation({
    mutationFn: (project: DeleteProjectInput) =>
      deleteProject({ organizationId: scope.organizationId, project }),
    onSuccess: async () => {
      await invalidateScope(scope);
      toast.success(i18n.t("projects.projectDeletedSuccess"));
    },
    onError: () => {
      toast.error(i18n.t("projects.failedToDeleteProject"));
    },
  });
};
